<template>
  <v-layout
    row
    justify-center
  >
    <v-dialog
      v-model="isOpen"
      persistent
      max-width="1000px"
    >
      <v-card class="create-bridge-signing">
        <v-form>
          <v-card-title class="toolbar primary white--text">
            <v-icon dark>
              note_add
            </v-icon>
            <h3
              v-if="!security.id"
              class="title ml-2"
            >
              Create Security
            </h3>
            <h3
              v-if="security.id"
              class="title ml-2"
            >
              Update Security
            </h3>
            <v-spacer />
            <v-btn
              color="secondary"
              flat
              @click="onToggleOpen"
            >
              X
            </v-btn>
          </v-card-title>
          <v-progress-linear
            v-if="isLoading"
            :indeterminate="true"
            class="ma-0"
          />

          <v-card-text>
            <v-container
              grid-list-md
              class="pa-0"
            >
              <v-layout
                wrap
                class="form-group"
              >
                <v-flex xs12>
                  <h6 class="subheading">
                    Security
                  </h6>
                  <p>
                    Please use as Security ID:
                    <ul>
                      <li>Loans: Project NID (e.g. 2766)</li>
                      <li>Yield/FinancingToken: Token Symbol (e.g. EXP12)</li>
                      <li>Baader: ISIN (e.g. DE000A2NBZ70)</li>
                    </ul>
                  </p>
                </v-flex>

                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.id"
                    label="ID (NID, Token Symbol or ISIN)"
                    :error-messages="getFieldErrors('id')"
                    :disabled="security.id"
                    @input="$v.formData.id.$touch()"
                    @blur="$v.formData.id.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.fp_id"
                    label="Financial Product ID"
                    :error-messages="getFieldErrors('fp_id')"
                    @input="$v.formData.fp_id.$touch()"
                    @blur="$v.formData.fp_id.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.name"
                    label="Name"
                    :error-messages="getFieldErrors('name')"
                    @input="$v.formData.name.$touch()"
                    @blur="$v.formData.name.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-select
                    v-model="formData.type"
                    :items="types()"
                    label="Type"
                    item-key="id"
                    item-text="name"
                    item-value="id"
                    :error-messages="getFieldErrors('type')"
                    @input="$v.formData.type.$touch()"
                    @blur="$v.formData.type.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-select
                    v-model="formData.custodian"
                    :items="custodians()"
                    label="Custodian"
                    item-key="id"
                    item-text="name"
                    item-value="id"
                    :error-messages="getFieldErrors('custodian')"
                    @input="$v.formData.custodian.$touch()"
                    @blur="$v.formData.custodian.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.notional"
                    label="Notional"
                    :error-messages="getFieldErrors('notional')"
                    @input="$v.formData.notional.$touch()"
                    @blur="$v.formData.notional.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.taxable_in"
                    label="Taxable In"
                    :error-messages="getFieldErrors('taxable_in')"
                    @input="$v.formData.taxable_in.$touch()"
                    @blur="$v.formData.taxable_in.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  sm6
                >
                  <v-select
                    v-model="formData.regulation_type"
                    :items="regulationTypes"
                    item-value="id"
                    label="Regulation Type"
                    item-key="id"
                    item-text="name"
                    :error-messages="getFieldErrors('regulation_type')"
                    @input="$v.formData.regulation_type.$touch()"
                    @blur="$v.formData.regulation_type.$touch()"
                  />
                </v-flex>
                <v-flex
                  v-if="formData.type === 'yield_baader'"
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.total_shares"
                    label="Total Shares"
                    :error-messages="getFieldErrors('total_shares')"
                    @input="$v.formData.total_shares.$touch()"
                    @blur="$v.formData.total_shares.$touch()"
                  />
                </v-flex>
                <v-flex
                  v-if="formData.type === 'loan'"
                  xs12
                  md6
                >
                  <v-checkbox
                    v-model="formData.silent_participation"
                    label="Silent Participation Security"
                    item-key="id"
                    item-text="name"
                    item-value="id"
                    :error-messages="getFieldErrors('silent_participation')"
                    @input="$v.formData.silent_participation.$touch()"
                    @blur="$v.formData.silent_participation.$touch()"
                  />
                </v-flex>
              </v-layout>
            </v-container>
          </v-card-text>

          <v-card-text>
            <v-container
              grid-list-md
              class="pa-0"
            >
              <v-layout
                wrap
                class="form-group"
              >
                <v-flex xs12>
                  <h6 class="subheading">
                    Issuer
                  </h6>
                </v-flex>
                <v-flex xs12>
                  <v-btn
                    v-if="hasCompanyId(formData)"
                    color="primary"
                    @click="removeCompanyId"
                  >
                    Remove Company ID
                  </v-btn>
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-autocomplete
                    v-model="formData.issuer.company_id"
                    :items="getCompaniesMinimal()"
                    label="Company ID"
                    :error-messages="getFieldErrors('issuer.company_id')"
                    item-text="name"
                    item-value="id"
                    @input="$v.formData.issuer.company_id.$touch()"
                    @blur="$v.formData.issuer.company_id.$touch()"
                  >
                    <template
                      slot="item"
                      slot-scope="data"
                    >
                      <v-list-tile-content>
                        <v-list-tile-title>
                          <b>{{ data.item.name }}</b> — {{ data.item.id }}
                        </v-list-tile-title>
                      </v-list-tile-content>
                    </template>
                  </v-autocomplete>
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.issuer.company_name"
                    label="Company Name"
                    :error-messages="getFieldErrors('issuer.company_name')"
                    :disabled="hasCompanyId(formData)"
                    @input="$v.formData.issuer.company_name.$touch()"
                    @blur="$v.formData.issuer.company_name.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.issuer.company_address"
                    label="Company Address"
                    :error-messages="getFieldErrors('issuer.company_address')"
                    :disabled="hasCompanyId(formData)"
                    @input="$v.formData.issuer.company_address.$touch()"
                    @blur="$v.formData.issuer.company_address.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.issuer.register_id"
                    label="Register ID"
                    :error-messages="getFieldErrors('issuer.register_id')"
                    :disabled="hasCompanyId(formData)"
                    @input="$v.formData.issuer.register_id.$touch()"
                    @blur="$v.formData.issuer.register_id.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.issuer.tax_identification"
                    label="Tax Identification"
                    :error-messages="getFieldErrors('issuer.tax_identification')"
                    :disabled="hasCompanyId(formData)"
                    @input="$v.formData.issuer.tax_identification.$touch()"
                    @blur="$v.formData.issuer.tax_identification.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.issuer.tax_office"
                    label="Tax Office"
                    :error-messages="getFieldErrors('issuer.tax_office')"
                    :disabled="hasCompanyId(formData)"
                    @input="$v.formData.issuer.tax_office.$touch()"
                    @blur="$v.formData.issuer.tax_office.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.issuer.ceo_name"
                    label="CEO Name"
                    :error-messages="getFieldErrors('issuer.ceo_name')"
                    :disabled="hasCompanyId(formData)"
                    @input="$v.formData.issuer.ceo_name.$touch()"
                    @blur="$v.formData.issuer.ceo_name.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.issuer.iban"
                    label="IBAN"
                    :error-messages="getFieldErrors('issuer.iban')"
                    :disabled="hasCompanyId(formData)"
                    @input="$v.formData.issuer.iban.$touch()"
                    @blur="$v.formData.issuer.iban.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.issuer.bic"
                    label="BIC"
                    :error-messages="getFieldErrors('issuer.bic')"
                    :disabled="hasCompanyId(formData)"
                    @input="$v.formData.issuer.bic.$touch()"
                    @blur="$v.formData.issuer.bic.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.issuer.bank_name"
                    label="Bank Name"
                    :error-messages="getFieldErrors('issuer.bank_name')"
                    :disabled="hasCompanyId(formData)"
                    @input="$v.formData.issuer.bank_name.$touch()"
                    @blur="$v.formData.issuer.bank_name.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.issuer.register_court"
                    label="Register Court"
                    :error-messages="getFieldErrors('issuer.register_court')"
                    :disabled="hasCompanyId(formData)"
                    @input="$v.formData.issuer.register_court.$touch()"
                    @blur="$v.formData.issuer.register_court.$touch()"
                  />
                </v-flex>
              </v-layout>
            </v-container>
          </v-card-text>
          <v-card-text>
            <v-alert
              v-for="e in error"
              :key="e.path"
              :value="error"
              type="error"
              color="error"
            >
              {{ e.message }}
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              color="secondary"
              flat
              @click="onToggleOpen"
            >
              Cancel
            </v-btn>
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  :disabled="isLoading || !$v.formData.$anyDirty"
                  :loading="isLoading"
                  color="primary"
                  @click="submit"
                  v-on="on"
                >
                  <span v-if="!security.id">Create Security</span>
                  <span v-if="security.id">Update Security</span>
                </v-btn>
              </template>
              <span>Validate and submit</span>
            </v-tooltip>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import { required, helpers, maxLength, minLength, decimal, between } from 'vuelidate/lib/validators';
import RegulationTypeEnum from '../../../../../enum/financialProducts/RegulationType';

const addressValidator = helpers.regex('address', /[a-z ]+[\d]+/i);
const containsCharsValidator = helpers.regex('chars', /[a-z]/i);

export default {
  name: 'CreateDialog',
  props: {
    security: {
      type: Object,
      required: true,
    },
    isOpen: {
      type: Boolean,
      required: true,
    },
    toggleOpen: {
      type: Function,
      required: true,
    },
  },
  data: () => ({
    isOpenProp: this.isOpen,
    isLoading: false,
    error: null,
    formData: {
      id: null,
      fp_id: '',
      name: '',
      type: '',
      custodian: '',
      regulation_type: '',
      notional: '',
      taxable_in: '',
      total_shares: '',
      issuer: {
        company_id: '',
        iban: '',
        bic: '',
        bank_name: '',
        ceo_name: '',
        company_address: '',
        company_name: '',
        register_court: '',
        register_id: '',
        tax_identification: '',
        tax_office: '',
      },
    },
  }),
  validations() {
    return {
      formData: {
        // Security
        id: { required },
        fp_id: { },
        name: { containsCharsValidator, maxLength: maxLength(50), required },
        type: { required },
        custodian: { required },
        notional: { decimal, between: between(1, 100000) },
        taxable_in: { required, minLength: minLength(3), maxLength: maxLength(3) },
        total_shares: { decimal, between: between(1, 100000) },
        regulation_type: { required },
        silent_participation: { },
        // Issuer
        issuer: {
          company_id: { },
          iban: { },
          bic: { },
          bank_name: { },
          ceo_name: { },
          company_address: { addressValidator },
          company_name: { containsCharsValidator },
          register_court: { },
          register_id: { },
          tax_identification: { },
          tax_office: { },
        },
      },
    };
  },
  computed: {
    validFields() {
      return Object.keys(this.$v.formData.$params).filter(
        item => !this.$v.formData[item].$invalid,
      );
    },
    errors() {
      return Object.keys(this.$v.formData.$params).filter(
        item => this.$v.formData[item].$invalid,
      );
    },
    regulationTypes() {
      return Object.keys(RegulationTypeEnum).map(key => ({
        name: RegulationTypeEnum[key],
        value: RegulationTypeEnum[key],
      }));
    },
  },
  mounted() {
    this.formData = {
      ...this.formData,
      ...(this.serializeSecurity(this.security)),
    };

    this.$v.$touch();

    this.loadData();
  },
  methods: {
    loadData() {
      this.$store.dispatch('fetchPatCompanyListMinimal');
    },
    getCompaniesMinimal() {
      return this.$store.getters.getCompaniesListMinimal;
    },
    types() {
      const data = this.$store.getters.getSecurityTypes;
      if (!data || !data.security || !data.security.types) {
        return [];
      }

      return this.transformEnumToSelectArray(data.security.types);
    },
    custodians() {
      const data = this.$store.getters.getSecurityTypes;
      if (!data || !data.security || !data.security.custodians) {
        return [];
      }

      return this.transformEnumToSelectArray(data.security.custodians);
    },
    hasCompanyId(values) {
      if (!values) {
        return false;
      }

      if (!values.issuer) {
        return false;
      }

      if (!values.issuer.company_id) {
        return false;
      }

      return values.issuer.company_id !== '';
    },
    transformEnumToSelectArray(options) {
      const newOptions = [];
      // eslint-disable-next-line no-restricted-syntax
      for (const [key, value] of Object.entries(options)) {
        newOptions.push({
          id: key,
          name: value,
        });
      }

      return newOptions;
    },
    getFieldErrors(field) {
      const errors = [];
      const formField = this.$v.formData[field];

      if (formField) {
        if (!formField.$dirty || !formField.$invalid) {
          return errors;
        }

        if (!formField.required) {
          errors.push('Required field');
        }

        if (formField.minLength !== undefined && !formField.minLength) {
          errors.push('Field has a minimum length');
        }

        if (formField.maxLength !== undefined && !formField.maxLength) {
          errors.push('Field has a maximum length');
        }

        if (formField.addressValidator !== undefined && !formField.addressValidator) {
          errors.push('Field must be a valid address (Chars & Numbers)');
        }

        if (formField.containsCharsValidator !== undefined && !formField.containsCharsValidator) {
          errors.push('Chars required');
        }
      }

      return errors;
    },
    getProperty(object, propString) {
      if (!propString) {
        return object;
      }

      let prop;
      const props = propString.split('.');

      let i;
      const iLen = props.length - 1;
      // eslint-disable-next-line no-plusplus
      for (i = 0; i < iLen; i++) {
        prop = props[i];

        const candidate = object[prop];
        if (candidate !== undefined) {
          object = candidate;
        } else {
          break;
        }
      }

      return object[props[i]];
    },
    onToggleOpen() {
      this.toggleOpen();
      this.error = null;
    },
    removeCompanyId() {
      this.formData.issuer.company_id = '';
      this.$v.$touch();
    },
    submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      this.isLoading = true;
      if (this.security.id) {
        this.$store
          .dispatch('updatePatSecurity', this.serializeFormData(this.formData))
          .then(() => {
            this.onToggleOpen();
          })
          .catch((err) => {
            this.error = err;
          })
          .finally(() => {
            this.isLoading = false;
          });
      } else {
        this.$store
          .dispatch('createPatSecurity', this.serializeFormData(this.formData))
          .then(() => {
            this.onToggleOpen();
          })
          .catch((err) => {
            this.error = err;
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
    serializeSecurity(security) {
      const { ...serializeSecurity } = security;

      return {
        ...serializeSecurity,
      };
    },
    serializeFormData(formData) {
      const { ...serializeFormData } = formData;

      return {
        ...serializeFormData,
      };
    },
  },
};
</script>

<style lang="scss">
    .create-bridge-signing {
        .v-card__title {
            padding: 24px;
        }
        .v-card__text {
            max-height: 65vh;
            overflow: auto;
            padding-top: 32px;
        }
        .v-card__actions {
            border-top: 1px solid rgba(0, 0, 0, 0.12);
        }
        .radio-label {
            position: absolute;
            font-size: 16px;
            text-align: left;
            max-width: 133%;
            height: 20px;
            left: 12px;
            line-height: 20px;
            color: rgba(0, 0, 0, 0.54);
            -webkit-transform: translateY(-18px) scale(0.75);
            transform: translateY(0px) translateX(-6px) scale(0.75);
        }
        .subheading {
            padding: 8px;
            background: white;
            color: rgba(0, 0, 0, 0.54);
            top: -20px;
            left: 8px;
            position: absolute;
            display: flex;
            align-items: center;
        }
        .form-group {
            padding: 16px;
            margin: 0 0 24px 0;
            border: 1px solid rgba(0, 0, 0, 0.12);
            border-radius: 5px;
            position: relative;
        }
        .container.grid-list-md .layout:not(:only-child) {
            margin: 0 0 24px 0;
        }
        .v-input--selection-controls.v-input .v-label {
            text-transform: capitalize;
        }
    }
</style>



// WEBPACK FOOTER //
// src/components/payoutsAndTaxes/payout/dialogs/security/CreateDialog.vue